import { defaultOperationDate, toOperationTimestamp } from '@/components/organisms/FishGroupForms';
import { type DomainFishType, FISH_TYPE_SHORT_NAMES } from '@/domain/cages';
import type { CreateSiteFishGroupInput } from '@/domain/fishGroups';

import type { EmptyPen } from '../../types';

export interface NewFishGroupFormValues {
    stockingCycleId: string;
    cageId: string;
    startDate: Date | null;
    fishTypeId: string;
    name: string;
    initialPopulationSize: string;
    reason: string;
    notes: string;
}

/** Salmon by short name (D17), else the type the backend flags as default, else the first. */
export function defaultFishTypeId(fishTypes: DomainFishType[]): number | null {
    const salmon = fishTypes.find((type) => type.shortName === FISH_TYPE_SHORT_NAMES.SALMON);
    return (salmon ?? fishTypes.find((type) => type.isDefault) ?? fishTypes[0])?.id ?? null;
}

export interface NewFishGroupDefaultsInput {
    cycleId: number | null;
    cageId: number | null;
    pens: EmptyPen[];
    fishTypes: DomainFishType[];
}

/** The pen the form opens on: the one it was opened from if still empty, else the first. */
export function defaultCageId(pens: EmptyPen[], preferred: number | null): number | null {
    return pens.some((pen) => pen.cageId === preferred) ? preferred : (pens[0]?.cageId ?? null);
}

/** A factory: the start date defaults to today, which a module constant would freeze. */
export function makeNewFishGroupDefaults(input: NewFishGroupDefaultsInput): NewFishGroupFormValues {
    const cageId = defaultCageId(input.pens, input.cageId);
    const fishTypeId = defaultFishTypeId(input.fishTypes);
    return {
        stockingCycleId: input.cycleId === null ? '' : String(input.cycleId),
        cageId: cageId === null ? '' : String(cageId),
        startDate: defaultOperationDate(),
        fishTypeId: fishTypeId === null ? '' : String(fishTypeId),
        name: '',
        initialPopulationSize: '',
        reason: '',
        notes: '',
    };
}

export function toCreateSiteFishGroupInput(
    values: NewFishGroupFormValues
): CreateSiteFishGroupInput | null {
    const stockingCycleId = Number(values.stockingCycleId);
    const cageId = Number(values.cageId);
    const fishTypeId = Number(values.fishTypeId);
    if (!stockingCycleId || !cageId || !fishTypeId || !values.startDate) return null;
    return {
        stockingCycleId,
        cageId,
        fishTypeId,
        name: values.name,
        startDate: toOperationTimestamp(values.startDate),
        ...(values.initialPopulationSize !== ''
            ? { initialPopulationSize: Number(values.initialPopulationSize) }
            : {}),
        reason: values.reason,
        notes: values.notes,
    };
}
